import { Box, IconButton, Link } from "@mui/material";
import CheckCircleIcon from "../icon-components/check_circle";
import ViewIcon from "../icon-components/ViewIcon";
import VideoModal from "../../components/custom-modal/VideoModal";
import CustomContentModal from "../../components/custom-content-modal/CustomContentModal";
import { useEffect, useState } from "react";
import { useGetVideoContentForAdminQuery } from "../../features/manage-milestones/GetMilestonesApiSlice";
import { enqueueSnackbar } from "notistack";
import { useAppSelector } from "../../store/hooks";
import { REGEX_VALIDATIONS } from "../../utility/constants/common-constants";
import {
  BTN_VERIFY_LINK,
  INVALID_URL_MSG,
} from "../../utility/constants/locale";

interface VerifyLinkProps {
  disabled: boolean;
  handleContentVerified: (status: boolean) => void;
  contentType: number;
  subs: string;
  contentId: string;
}

const VerifyLink = ({
  disabled,
  handleContentVerified,
  contentType,
  subs,
  contentId,
}: VerifyLinkProps) => {
  const accessToken = useAppSelector(
    (state) => state.adminAuth.user?.accessToken!
  );
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [articleModalOpen, setArticleModalOpen] = useState(false);
  const [videoContent, setVideoContent] = useState("");
  const [articleContent, setArticleContent] = useState("");
  const [isContentVerified, setIsContentVerified] = useState(false);
  const [selectedId, setSelectedId] = useState({
    taskCode: "",
    type: "",
    langauge: "en",
  });

  const { data: educationalData, error: educationalDataError } =
    useGetVideoContentForAdminQuery(
      { ...selectedId, accessToken },
      {
        skip: !selectedId.taskCode, // Skip the query if no taskCode is set
      }
    );

  const handleOpenVideoModal = () => setVideoModalOpen(true);
  const handleOpenArticleModal = () => setArticleModalOpen(true);

  const handleCloseModals = () => {
    setVideoModalOpen(false);
    setArticleModalOpen(false);
  };

  const handleViewIconClick = () => {
    if (contentType === 1) {
      handleOpenVideoModal();
    } else if (contentType === 2) {
      handleOpenArticleModal();
    }
  };

  useEffect(() => {
    if (!selectedId.taskCode) return;

    if (educationalData) {
      if (contentType === 1 && educationalData.videoUrl) {
        setIsContentVerified(true);
        setVideoContent(educationalData.videoUrl);
        handleContentVerified(true);
      } else if (contentType === 2 && educationalData.fullHtml) {
        setIsContentVerified(true);
        setArticleContent(educationalData.fullHtml);
        handleContentVerified(true);
      } else {
        setIsContentVerified(false);
        handleContentVerified(false);
      }
    } else if (educationalDataError) {
      setIsContentVerified(false);
      handleContentVerified(false);
      enqueueSnackbar("Failed to verify link", { variant: "error" });
    }
  }, [
    selectedId,
    educationalData,
    educationalDataError,
    contentType,
    handleContentVerified,
  ]);

  const handleVerifyLinkClick = () => {
    if (disabled) return;
    if (contentType === 3) {
      const url = contentId;

      if (url && REGEX_VALIDATIONS.URL_REGEX.test(url)) {
        window.open(url, "_blank", "noopener,noreferrer");
        setIsContentVerified(true);
        handleContentVerified(true);
      } else {
        setIsContentVerified(false);
        handleContentVerified(false);
        enqueueSnackbar(INVALID_URL_MSG, { variant: "error" });
      }
    } else {
      setSelectedId({
        taskCode: contentId,
        type: contentType === 1 ? "videos" : "articles",
        langauge: "en",
      });
    }
  };

  return (
    <Box display="flex" flexDirection="column" gap={2} marginTop={2}>
      {/* Verification Link Section */}
      <Box display="flex" alignItems="center" gap={1.5}>
        <CheckCircleIcon
          width="20"
          height="20"
          fill={disabled || !isContentVerified ? "#BDBDBD" : "#4CAF50"}
        />
        <Link
          onClick={handleVerifyLinkClick}
          sx={{
            fontWeight: 600,
            fontSize: "14px",
            color: disabled ? "#BDBDBD" : "#A41E35",
            pointerEvents: disabled ? "none" : "auto",
            cursor: "pointer",
          }}
        >
          {BTN_VERIFY_LINK}
        </Link>
        <IconButton
          onClick={handleViewIconClick}
          disabled={disabled || !isContentVerified}
        >
          <ViewIcon
            fill={disabled || !isContentVerified ? "#BDBDBD" : "#A41E35"}
          />
        </IconButton>
      </Box>

      {/* Content Handling Section */}
      {contentType === 1 && (
        <VideoModal
          open={videoModalOpen}
          onClose={handleCloseModals}
          videoUrl={videoContent}
          subtitle={subs}
          modalTitle={educationalData ? educationalData.title : ""}
          modalSummarry={educationalData ? educationalData.summary : ""}
        />
      )}
      {contentType === 2 && (
        <CustomContentModal
          open={articleModalOpen}
          onClose={handleCloseModals}
          htmlContent={articleContent}
          subtitle={subs}
          modalTitle={educationalData ? educationalData.title : ""}
        />
      )}
    </Box>
  );
};

export default VerifyLink;
